import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Phone, PhoneCall, PhoneOff, Mic, MicOff, Pause, Play,
  Search, User, Users, Briefcase, FileText, MessageSquare,
  Plus, Send, Save, Clock, CheckCircle, AlertCircle,
  ExternalLink, ChevronRight, Sparkles, Bot, X, Volume2,
  VolumeX, Grid3X3, ArrowLeftRight, Building, Mail, MapPin,
  Hash, RefreshCw, Loader2, StickyNote, Shield, PhoneMissed,
  ListTodo, ChevronDown, ChevronUp, SkipForward, Bell, Timer,
  Inbox, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Call States
const CALL_STATES = {
  idle: { label: 'Ready', color: 'bg-slate-500' },
  ringing: { label: 'Ringing...', color: 'bg-yellow-500 animate-pulse' },
  connected: { label: 'Connected', color: 'bg-green-500' },
  on_hold: { label: 'On Hold', color: 'bg-orange-500' },
  wrap_up: { label: 'Wrap Up', color: 'bg-blue-500' },
};

// Dialpad buttons
const DIALPAD = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#']
];

// Format phone number for display
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// ==========================================
// Header Search Dropdown Component
// ==========================================
const HeaderSearchDropdown = ({ onSelectResult, onLoadToDialer }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const res = await axios.get(`${API}/call-center/workstation/lookup`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data.results || []);
        setIsOpen(true);
        setSelectedIndex(-1);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };
  
  const handleSelect = (result) => {
    onSelectResult(result);
    if (result.phone) {
      onLoadToDialer(result.phone, result);
    }
    setQuery('');
    setIsOpen(false);
  };
  
  const getTypeIcon = (type) => {
    switch (type) {
      case 'customer': return <User className="h-4 w-4 text-blue-500" />;
      case 'job': return <Briefcase className="h-4 w-4 text-green-500" />;
      case 'contact': return <Users className="h-4 w-4 text-purple-500" />;
      case 'lead': return <Users className="h-4 w-4 text-yellow-500" />;
      default: return <User className="h-4 w-4" />;
    }
  };
  
  // Group results by type
  const groupedResults = useMemo(() => {
    const groups = { customer: [], job: [], contact: [], lead: [] };
    results.forEach(r => {
      if (groups[r.type]) {
        groups[r.type].push(r);
      }
    });
    return groups;
  }, [results]);
  
  const hasResults = results.length > 0;
  
  return (
    <div ref={dropdownRef} className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder="Search customer, job, phone, address..."
          className="pl-9 pr-8 h-9 bg-muted/50"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      
      {/* Dropdown Results */}
      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-popover border rounded-lg shadow-lg z-50 max-h-[400px] overflow-hidden">
          {!hasResults ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {loading ? 'Searching...' : 'No results found'}
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              {/* Customers */}
              {groupedResults.customer.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                    Customers
                  </div>
                  {groupedResults.customer.map((result, idx) => {
                    const globalIdx = results.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={`w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-3 ${
                          selectedIndex === globalIdx ? 'bg-muted' : ''
                        }`}
                      >
                        {getTypeIcon(result.type)}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{result.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.phone && formatPhoneDisplay(result.phone)}
                            {result.address && ` • ${result.address}`}
                          </div>
                        </div>
                        {result.phone && (
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Jobs */}
              {groupedResults.job.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                    Jobs
                  </div>
                  {groupedResults.job.map((result) => {
                    const globalIdx = results.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={`w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-3 ${
                          selectedIndex === globalIdx ? 'bg-muted' : ''
                        }`}
                      >
                        {getTypeIcon(result.type)}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{result.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.status && <Badge variant="outline" className="text-xs mr-1">{result.status}</Badge>}
                            {result.address}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Contacts */}
              {groupedResults.contact.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                    Contacts
                  </div>
                  {groupedResults.contact.map((result) => {
                    const globalIdx = results.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={`w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-3 ${
                          selectedIndex === globalIdx ? 'bg-muted' : ''
                        }`}
                      >
                        {getTypeIcon(result.type)}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{result.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.phone && formatPhoneDisplay(result.phone)}
                            {result.email && ` • ${result.email}`}
                          </div>
                        </div>
                        {result.phone && (
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Leads */}
              {groupedResults.lead.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                    Leads
                  </div>
                  {groupedResults.lead.map((result) => {
                    const globalIdx = results.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={`w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-3 ${
                          selectedIndex === globalIdx ? 'bg-muted' : ''
                        }`}
                      >
                        {getTypeIcon(result.type)}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{result.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.phone && formatPhoneDisplay(result.phone)}
                          </div>
                        </div>
                        {result.phone && (
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// Dialer Column Component
// ==========================================
const DialerColumn = ({ 
  callState, 
  setCallState, 
  phoneNumber, 
  setPhoneNumber,
  callDuration,
  isMuted,
  setIsMuted,
  isOnHold,
  setIsOnHold,
  onCallStart,
  onCallEnd,
  selectedContact,
  queuedItem
}) => {
  const handleDial = (digit) => {
    if (callState === 'idle') {
      setPhoneNumber(prev => prev + digit);
    }
  };
  
  const handleCall = () => {
    if (!phoneNumber) {
      toast.error('Enter a phone number');
      return;
    }
    setCallState('ringing');
    // Simulate connection after 2 seconds (demo mode)
    setTimeout(() => {
      setCallState('connected');
      onCallStart();
      toast.success('Call connected (Demo Mode)');
    }, 2000);
  };
  
  const handleHangup = () => {
    setCallState('wrap_up');
    onCallEnd();
    toast.info('Call ended - Complete wrap-up notes');
    // Auto-reset after 30 seconds
    setTimeout(() => {
      setCallState('idle');
    }, 30000);
  };
  
  const formatDuration = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
  const stateInfo = CALL_STATES[callState] || CALL_STATES.idle;
  
  return (
    <div className="flex flex-col h-full">
      {/* Call Status Header */}
      <div className={`p-4 rounded-t-lg ${stateInfo.color} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            <span className="font-medium">{stateInfo.label}</span>
          </div>
          {callState !== 'idle' && (
            <span className="font-mono text-lg">{formatDuration(callDuration)}</span>
          )}
        </div>
        {(selectedContact || queuedItem) && (
          <div className="mt-2 text-sm opacity-90">
            {selectedContact?.name || queuedItem?.name || queuedItem?.customer_name}
          </div>
        )}
      </div>
      
      {/* Phone Input / Display */}
      <Card className="flex-1 rounded-t-none border-t-0">
        <CardContent className="p-4 space-y-4">
          {/* Phone Number Display */}
          <div className="text-center">
            {callState === 'idle' ? (
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                className="text-center text-xl font-mono h-12"
              />
            ) : (
              <div className="text-2xl font-mono font-bold py-2">
                {formatPhoneDisplay(phoneNumber) || 'Unknown'}
              </div>
            )}
          </div>
          
          {/* Queued Item Badge */}
          {queuedItem && callState === 'idle' && (
            <div className="bg-primary/10 rounded-lg p-2 flex items-center gap-2 text-sm">
              <Bell className="h-4 w-4 text-primary" />
              <span className="text-primary font-medium">
                {queuedItem.type === 'callback' ? 'Callback' : 'Missed Call'} loaded
              </span>
            </div>
          )}
          
          {/* Dialpad */}
          {callState === 'idle' && (
            <div className="grid grid-cols-3 gap-2">
              {DIALPAD.flat().map((digit) => (
                <Button
                  key={digit}
                  variant="outline"
                  className="h-12 text-lg font-semibold"
                  onClick={() => handleDial(digit)}
                >
                  {digit}
                </Button>
              ))}
            </div>
          )}
          
          {/* Call Controls */}
          <div className="flex gap-2 justify-center pt-2">
            {callState === 'idle' ? (
              <Button
                size="lg"
                className="flex-1 bg-green-500 hover:bg-green-600 h-12"
                onClick={handleCall}
              >
                <Phone className="h-5 w-5 mr-2" />
                Call
              </Button>
            ) : callState === 'ringing' ? (
              <Button
                size="lg"
                variant="destructive"
                className="flex-1 h-12"
                onClick={() => setCallState('idle')}
              >
                <PhoneOff className="h-5 w-5 mr-2" />
                Cancel
              </Button>
            ) : callState === 'wrap_up' ? (
              <Button
                size="lg"
                variant="outline"
                className="flex-1 h-12"
                onClick={() => setCallState('idle')}
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Complete
              </Button>
            ) : (
              <>
                <Button
                  variant={isMuted ? 'destructive' : 'outline'}
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setIsMuted(!isMuted)}
                >
                  {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
                <Button
                  variant={isOnHold ? 'secondary' : 'outline'}
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setIsOnHold(!isOnHold)}
                >
                  {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => toast.info('Keypad (Demo)')}
                >
                  <Grid3X3 className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => toast.info('Transfer (Demo)')}
                >
                  <ArrowLeftRight className="h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="destructive"
                  className="flex-1 h-12"
                  onClick={handleHangup}
                >
                  <PhoneOff className="h-5 w-5 mr-2" />
                  End
                </Button>
              </>
            )}
          </div>
          
          {/* Demo Mode Notice */}
          <div className="text-center text-xs text-muted-foreground pt-2 border-t">
            <Badge variant="outline" className="text-xs">
              <Shield className="h-3 w-3 mr-1" />
              Demo Mode - Calls Simulated
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==========================================
// Call Queue Column Component (Replaces old Lookup)
// ==========================================
const CallQueueColumn = ({ 
  onLoadToDialer, 
  onSelectResult,
  selectedResult,
  currentUserId,
  onNewContact
}) => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [missedCalls, setMissedCalls] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Fetch queue data
  const fetchQueueData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch missed calls, callbacks, and inbox in parallel
      const [missedRes, callbacksRes, inboxRes] = await Promise.all([
        axios.get(`${API}/call-center/workstation/queue/missed`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { calls: [] } })),
        axios.get(`${API}/call-center/workstation/queue/callbacks`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { callbacks: [] } })),
        axios.get(`${API}/call-center/workstation/queue/inbox`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { messages: [] } }))
      ]);
      
      setMissedCalls(missedRes.data.calls || []);
      setCallbacks(callbacksRes.data.callbacks || []);
      setInboxMessages(inboxRes.data.messages || []);
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchQueueData();
  }, [fetchQueueData]);
  
  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchQueueData(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchQueueData]);
  
  // Get next item based on active tab
  const getNextItem = () => {
    if (activeTab === 'inbox' && inboxMessages.length > 0) {
      // Get first unread inbox message
      return { ...inboxMessages[0], type: 'inbox' };
    } else if (activeTab === 'callbacks' && callbacks.length > 0) {
      // Get first open callback (sorted by due date)
      return { ...callbacks[0], type: 'callback' };
    } else if (activeTab === 'missed' && missedCalls.length > 0) {
      // Get most recent missed call
      return { ...missedCalls[0], type: 'missed' };
    }
    return null;
  };
  
  // Handle Call Next
  const handleCallNext = () => {
    const nextItem = getNextItem();
    if (!nextItem) {
      toast.info('No items in queue');
      return;
    }
    
    const phone = nextItem.phone || nextItem.caller_phone || nextItem.phone_number;
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    
    onLoadToDialer(phone, nextItem);
    toast.success(`Loaded ${nextItem.type === 'callback' ? 'callback' : 'missed call'} — press Call to begin`);
  };
  
  // Handle load single item
  const handleLoadItem = (item, type) => {
    const phone = item.phone || item.caller_phone || item.phone_number;
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    
    onLoadToDialer(phone, { ...item, type });
    toast.success('Number loaded to dialer');
  };
  
  // Handle callback actions
  const handleMarkDone = async (callbackId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/callbacks/${callbackId}`, 
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Callback marked as done');
      fetchQueueData(true);
    } catch (err) {
      toast.error('Failed to update callback');
    }
  };
  
  const handleSnooze = async (callbackId) => {
    try {
      const token = localStorage.getItem('access_token');
      // Snooze for 1 hour
      const snoozedUntil = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      await axios.patch(`${API}/call-center/callbacks/${callbackId}`, 
        { status: 'snoozed', snoozed_until: snoozedUntil },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Callback snoozed for 1 hour');
      fetchQueueData(true);
    } catch (err) {
      toast.error('Failed to snooze callback');
    }
  };
  
  const totalItems = inboxMessages.length + missedCalls.length + callbacks.length;
  
  return (
    <div className="flex flex-col h-full">
      {/* Queue Header */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-primary" />
              <span className="font-semibold">Call Queue</span>
              {totalItems > 0 && (
                <Badge variant="secondary">{totalItems}</Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => fetchQueueData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onNewContact}>
              <Plus className="h-4 w-4 mr-1" />
              New Contact
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Selected Context Card */}
      {selectedResult && (
        <Card className="mb-3">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <Badge className="bg-blue-500">{selectedResult.type}</Badge>
              <Button variant="ghost" size="sm" onClick={() => onSelectResult(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <h4 className="font-semibold text-sm mb-1">{selectedResult.name}</h4>
            <div className="text-xs text-muted-foreground space-y-0.5">
              {selectedResult.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {formatPhoneDisplay(selectedResult.phone)}
                </div>
              )}
              {selectedResult.address && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{selectedResult.address}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Queue Tabs */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="border-b px-2 pt-2">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="inbox" className="text-xs">
                <Inbox className="h-3 w-3 mr-1" />
                Inbox ({inboxMessages.length})
              </TabsTrigger>
              <TabsTrigger value="missed" className="text-xs">
                <PhoneMissed className="h-3 w-3 mr-1" />
                Missed ({missedCalls.length})
              </TabsTrigger>
              <TabsTrigger value="callbacks" className="text-xs">
                <Bell className="h-3 w-3 mr-1" />
                Callbacks ({callbacks.length})
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Inbox Tab */}
          <TabsContent value="inbox" className="flex-1 m-0 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : inboxMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Inbox className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No messages in inbox</p>
                <p className="text-xs mt-1">New leads and messages will appear here</p>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="divide-y">
                  {inboxMessages.map((msg) => (
                    <div key={msg.id} className="p-3 hover:bg-muted/50">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-sm truncate">
                              {msg.sender_name || msg.from_name || msg.customer_name || 'Unknown'}
                            </div>
                            {msg.is_unread && (
                              <span className="w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatPhoneDisplay(msg.phone || msg.from_phone)}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatRelativeTime(msg.created_at || msg.received_at)}
                        </div>
                      </div>
                      
                      {msg.preview && (
                        <p className="text-xs text-muted-foreground truncate mb-2">
                          {msg.preview || msg.message?.slice(0, 50)}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">
                          {msg.type || msg.channel || 'Message'}
                        </Badge>
                        <div className="flex-1" />
                        {(msg.phone || msg.from_phone) && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs"
                            onClick={() => handleLoadItem(msg, 'inbox')}
                          >
                            <Phone className="h-3 w-3 mr-1" />
                            Call
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 text-xs"
                          onClick={() => toast.info('View message details')}
                        >
                          <MessageCircle className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
          
          {/* Missed Calls Tab */}
          <TabsContent value="missed" className="flex-1 m-0 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : missedCalls.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <PhoneMissed className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No missed calls</p>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="divide-y">
                  {missedCalls.map((call) => (
                    <div key={call.id} className="p-3 hover:bg-muted/50">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {call.caller_name || call.customer_name || 'Unknown Caller'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatPhoneDisplay(call.caller_phone || call.phone)}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatRelativeTime(call.created_at || call.missed_at)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 mt-2">
                        {call.customer_id && (
                          <Badge variant="outline" className="text-xs">Customer</Badge>
                        )}
                        {call.job_id && (
                          <Badge variant="outline" className="text-xs">Job</Badge>
                        )}
                        <div className="flex-1" />
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs"
                          onClick={() => handleLoadItem(call, 'missed')}
                        >
                          <Phone className="h-3 w-3 mr-1" />
                          Load
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
          
          {/* Callbacks Tab */}
          <TabsContent value="callbacks" className="flex-1 m-0 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : callbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No callbacks scheduled</p>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="divide-y">
                  {callbacks.map((cb) => (
                    <div key={cb.id} className="p-3 hover:bg-muted/50">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {cb.contact_name || cb.customer_name || 'Unknown'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatPhoneDisplay(cb.phone_number || cb.phone)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Timer className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(cb.scheduled_at || cb.due_at)}
                          </span>
                        </div>
                      </div>
                      
                      {cb.notes && (
                        <p className="text-xs text-muted-foreground truncate mb-2">
                          {cb.notes || cb.reason}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs"
                          onClick={() => handleLoadItem(cb, 'callback')}
                        >
                          <Phone className="h-3 w-3 mr-1" />
                          Load
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 text-xs"
                          onClick={() => handleMarkDone(cb.id)}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Done
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 text-xs"
                          onClick={() => handleSnooze(cb.id)}
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          Snooze
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Call Next Footer */}
        <div className="border-t p-3 bg-muted/30">
          <Button 
            className="w-full" 
            onClick={handleCallNext}
            disabled={totalItems === 0}
          >
            <SkipForward className="h-4 w-4 mr-2" />
            Call Next
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ==========================================
// AI Assistant + Scripts + Notes Column
// ==========================================
const AssistantColumn = ({ 
  scripts, 
  objections, 
  callContext,
  notes,
  setNotes,
  onSaveNotes,
  permissions
}) => {
  const [activeTab, setActiveTab] = useState('ai');
  const [aiQuery, setAiQuery] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [aiMessages]);
  
  const handleAiQuery = async () => {
    if (!aiQuery.trim()) return;
    
    const userMessage = { role: 'user', content: aiQuery };
    setAiMessages(prev => [...prev, userMessage]);
    setAiQuery('');
    setAiLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(`${API}/call-center/workstation/ai-assist`, {
        query: aiQuery,
        context: callContext
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        suggestions: res.data.suggestions
      }]);
    } catch (err) {
      console.error('AI error:', err);
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Group objections by category
  const objectionCategories = ['all', 'price', 'timing', 'trust', 'comparison', 'insurance', 'other'];
  const filteredObjections = objections.filter(
    o => selectedCategory === 'all' || o.category === selectedCategory
  );
  
  return (
    <div className="flex flex-col h-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger value="ai" className="text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            AI
          </TabsTrigger>
          <TabsTrigger value="scripts" className="text-xs">
            <FileText className="h-3 w-3 mr-1" />
            Scripts
          </TabsTrigger>
          <TabsTrigger value="objections" className="text-xs">
            <Shield className="h-3 w-3 mr-1" />
            Objections
          </TabsTrigger>
          <TabsTrigger value="notes" className="text-xs">
            <StickyNote className="h-3 w-3 mr-1" />
            Notes
          </TabsTrigger>
        </TabsList>
        
        {/* AI Assistant Tab */}
        <TabsContent value="ai" className="flex-1 flex flex-col mt-2 data-[state=inactive]:hidden">
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardHeader className="py-2 px-3 border-b">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Sage - AI Assistant</span>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              {/* Messages */}
              <ScrollArea className="flex-1 p-3">
                {aiMessages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Ask Sage for help during your call</p>
                    <div className="mt-3 space-y-1 text-xs">
                      <p>"How do I handle a price objection?"</p>
                      <p>"What are our warranty terms?"</p>
                      <p>"Suggest next steps for this lead"</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {aiMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg p-3 text-sm ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : msg.isError
                              ? 'bg-red-500/10 text-red-500'
                              : 'bg-muted'
                          }`}
                        >
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                          {msg.suggestions?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-current/10 space-y-1">
                              {msg.suggestions.map((s, j) => (
                                <Button
                                  key={j}
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-xs h-auto py-1"
                                  onClick={() => setAiQuery(s)}
                                >
                                  <ChevronRight className="h-3 w-3 mr-1" />
                                  {s}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg p-3">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Input */}
              <div className="p-3 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Ask Sage for help..."
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAiQuery()}
                    disabled={aiLoading}
                  />
                  <Button onClick={handleAiQuery} disabled={aiLoading || !aiQuery.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Scripts Tab */}
        <TabsContent value="scripts" className="flex-1 flex flex-col mt-2 data-[state=inactive]:hidden">
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardContent className="flex-1 flex p-0 overflow-hidden">
              {/* Scripts List */}
              <div className="w-1/3 border-r">
                <ScrollArea className="h-full">
                  <div className="p-2 space-y-1">
                    {scripts.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        No scripts available
                      </div>
                    ) : (
                      scripts.map((script) => (
                        <button
                          key={script.id}
                          onClick={() => setSelectedScript(script)}
                          className={`w-full text-left p-2 rounded text-sm transition-colors ${
                            selectedScript?.id === script.id
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <div className="font-medium truncate">{script.name}</div>
                          <div className="text-xs opacity-70">{script.script_type}</div>
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Script Content */}
              <div className="flex-1">
                <ScrollArea className="h-full p-4">
                  {selectedScript ? (
                    <div>
                      <h3 className="font-semibold mb-2">{selectedScript.name}</h3>
                      <Badge variant="outline" className="mb-3">{selectedScript.script_type}</Badge>
                      <div className="text-sm whitespace-pre-wrap">
                        {selectedScript.content || 'No content'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Select a script to view</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Objections Tab */}
        <TabsContent value="objections" className="flex-1 flex flex-col mt-2 data-[state=inactive]:hidden">
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              {/* Category Filter */}
              <div className="p-2 border-b">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {objectionCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Objections List */}
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-2">
                  {filteredObjections.map((obj) => (
                    <Card key={obj.id} className="p-3">
                      <div className="flex items-start gap-2 mb-2">
                        <Badge variant="outline" className="text-xs shrink-0">
                          {obj.category}
                        </Badge>
                        <span className="font-medium text-sm">{obj.title}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{obj.response}</p>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Notes Tab */}
        <TabsContent value="notes" className="flex-1 flex flex-col mt-2 data-[state=inactive]:hidden">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="py-2 px-3 border-b">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Call Notes</span>
                <Button size="sm" variant="outline" onClick={onSaveNotes}>
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 p-0">
              <Textarea
                placeholder="Enter notes for this call..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-full min-h-[200px] rounded-none border-0 resize-none focus-visible:ring-0"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==========================================
// New Contact Dialog
// ==========================================
const NewContactDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    company: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    if (!form.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(`${API}/call-center/workstation/contacts`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact created');
      onCreated(res.data.contact);
      onOpenChange(false);
      setForm({ first_name: '', last_name: '', phone: '', email: '', address: '', company: '', notes: '' });
    } catch (err) {
      toast.error('Failed to create contact');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Contact</DialogTitle>
          <DialogDescription>Add a new contact to the system</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Company</Label>
            <Input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// Main Work Station Component
// ==========================================
export default function WorkStation() {
  const { user } = useAuth();
  
  // Bootstrap data
  const [bootstrapData, setBootstrapData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Call state
  const [callState, setCallState] = useState('idle');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [currentCallId, setCurrentCallId] = useState(null);
  
  // Selected context and queued item
  const [selectedResult, setSelectedResult] = useState(null);
  const [queuedItem, setQueuedItem] = useState(null);
  const [newContactOpen, setNewContactOpen] = useState(false);
  
  // Notes
  const [notes, setNotes] = useState('');
  
  // Heartbeat interval ref
  const heartbeatRef = useRef(null);
  const callTimerRef = useRef(null);
  
  // Bootstrap on mount
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const [bootstrapRes] = await Promise.all([
          axios.get(`${API}/call-center/workstation/bootstrap`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          // Mark workstation as open
          axios.post(`${API}/call-center/workstation/presence/open`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setBootstrapData(bootstrapRes.data);
      } catch (err) {
        console.error('Bootstrap error:', err);
        toast.error('Failed to initialize Work Station');
      } finally {
        setLoading(false);
      }
    };
    
    bootstrap();
    
    // Start heartbeat
    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('access_token');
        await axios.post(`${API}/call-center/workstation/presence/heartbeat`, {
          current_call_id: currentCallId,
          call_state: callState
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Heartbeat error:', err);
      }
    };
    
    heartbeatRef.current = setInterval(sendHeartbeat, 20000);
    
    // Cleanup on unmount
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      
      // Mark workstation as closed
      const token = localStorage.getItem('access_token');
      axios.post(`${API}/call-center/workstation/presence/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(console.error);
    };
  }, []);
  
  // Call timer
  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      if (callState === 'idle') setCallDuration(0);
    }
    
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [callState]);
  
  // Update heartbeat with current call state
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.post(`${API}/call-center/workstation/presence/heartbeat`, {
      current_call_id: currentCallId,
      call_state: callState
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(console.error);
  }, [callState, currentCallId]);
  
  const handleCallStart = () => {
    const callId = `call-${Date.now()}`;
    setCurrentCallId(callId);
    setQueuedItem(null); // Clear queued item on call start
  };
  
  const handleCallEnd = () => {
    if (notes.trim() && currentCallId) {
      handleSaveNotes();
    }
    setCurrentCallId(null);
  };
  
  const handleSaveNotes = async () => {
    if (!notes.trim()) {
      toast.info('No notes to save');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/workstation/call-notes`, {
        call_id: currentCallId || `manual-${Date.now()}`,
        notes: notes,
        customer_id: selectedResult?.type === 'customer' ? selectedResult.id : null,
        job_id: selectedResult?.type === 'job' ? selectedResult.id : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notes saved');
    } catch (err) {
      console.error('Save notes error:', err);
      toast.error('Failed to save notes');
    }
  };
  
  const handleContactCreated = (contact) => {
    setSelectedResult({
      type: 'contact',
      id: contact.id,
      name: `${contact.first_name} ${contact.last_name}`.trim(),
      phone: contact.phone,
      email: contact.email,
      address: contact.address
    });
    if (contact.phone) {
      setPhoneNumber(contact.phone);
    }
  };
  
  // Load number to dialer (from search or queue)
  const handleLoadToDialer = (phone, item) => {
    const normalizedPhone = phone.replace(/\D/g, '');
    setPhoneNumber(normalizedPhone);
    setQueuedItem(item);
    
    // If item has customer/job info, set as selected result
    if (item && (item.customer_id || item.type === 'customer' || item.type === 'job')) {
      setSelectedResult({
        type: item.type || 'contact',
        id: item.customer_id || item.id,
        name: item.name || item.customer_name || item.caller_name || item.contact_name,
        phone: phone,
        address: item.address
      });
    }
  };
  
  // Build call context for AI
  const callContext = {
    customer: selectedResult?.type === 'customer' ? selectedResult : null,
    job: selectedResult?.type === 'job' ? selectedResult : null,
    call_type: queuedItem?.type || 'discovery',
    notes: notes
  };
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Initializing Work Station...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header with Global Search */}
      <div className="h-14 border-b bg-card flex items-center justify-between px-4 gap-4">
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5 text-primary" />
          <span className="font-semibold">RhinoShield Work Station</span>
          <Badge variant="outline" className={CALL_STATES[callState].color + ' text-white border-0'}>
            {CALL_STATES[callState].label}
          </Badge>
        </div>
        
        {/* Global Search in Header */}
        <HeaderSearchDropdown
          onSelectResult={setSelectedResult}
          onLoadToDialer={handleLoadToDialer}
        />
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs">
                {bootstrapData?.user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <span>{bootstrapData?.user?.name || 'Agent'}</span>
          </div>
          <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
            Online
          </Badge>
        </div>
      </div>
      
      {/* Main 3-Column Layout */}
      <div className="flex-1 flex overflow-hidden p-4 gap-4">
        {/* Column A - Dialer */}
        <div className="w-[320px] shrink-0">
          <DialerColumn
            callState={callState}
            setCallState={setCallState}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            callDuration={callDuration}
            isMuted={isMuted}
            setIsMuted={setIsMuted}
            isOnHold={isOnHold}
            setIsOnHold={setIsOnHold}
            onCallStart={handleCallStart}
            onCallEnd={handleCallEnd}
            selectedContact={selectedResult}
            queuedItem={queuedItem}
          />
        </div>
        
        {/* Column B - Call Queue (Replaces old Lookup) */}
        <div className="w-[380px] shrink-0">
          <CallQueueColumn
            onLoadToDialer={handleLoadToDialer}
            onSelectResult={setSelectedResult}
            selectedResult={selectedResult}
            currentUserId={bootstrapData?.user?.id}
            onNewContact={() => setNewContactOpen(true)}
          />
        </div>
        
        {/* Column C - AI + Scripts + Notes */}
        <div className="flex-1 min-w-[350px]">
          <AssistantColumn
            scripts={bootstrapData?.scripts || []}
            objections={bootstrapData?.objections || []}
            callContext={callContext}
            notes={notes}
            setNotes={setNotes}
            onSaveNotes={handleSaveNotes}
            permissions={bootstrapData?.permissions || {}}
          />
        </div>
      </div>
      
      {/* New Contact Dialog */}
      <NewContactDialog
        open={newContactOpen}
        onOpenChange={setNewContactOpen}
        onCreated={handleContactCreated}
      />
    </div>
  );
}
